import { Component } from "react";
import Popup from "../../common/Popup";
import {
  DKInputForm,
  DKLabel,
  DKButton,
  DKIcons,
  INPUT_TYPE,
  INPUT_VIEW_DIRECTION,
  showAlert
} from "deskera-ui-library";
import Utility from "../../../utility/Utility";
import { JobPipelineManager } from "../../../managers/JobPipelineManager";

interface IStageChangeTriggerProps {
  stepData: any;
  onClose: () => void;
  onSave: (data: any) => void;
}

interface IStageChangeTriggerState {
  fields: any[];
}

const DEFAULT_STAGE = { id: "any", name: "Any" };
export enum FIELD_IDENTIFIERS {
  PIPELINE = "jobPipelineId",
  PREV_STAGE = "oldStageId",
  NEW_STAGE = "newStageId",
}

class StageChangeTriggerPopup extends Component<
  IStageChangeTriggerProps,
  IStageChangeTriggerState
> {
  pipelineData: any[] = JobPipelineManager.processJobPipelineData();
  selectedPipeline: any = null;

  constructor(props) {
    super(props);

    this.selectedPipeline = this.pipelineData[0];

    this.state = {
      fields: [
        {
          title: "Pipeline",
          fieldName: FIELD_IDENTIFIERS.PIPELINE,
          value: this.selectedPipeline,
          placeholder: "Select a pipeline",
          type: INPUT_TYPE.DROPDOWN,
          required: true,
          dropdownConfig: {
            data: this.pipelineData,
            allowSearch: true,
            searchableKey: "name",
          },
        },
        {
          title: "From stage",
          fieldName: FIELD_IDENTIFIERS.PREV_STAGE,
          value: DEFAULT_STAGE,
          placeholder: "Select previous stage",
          type: INPUT_TYPE.DROPDOWN,
          required: true,
          dropdownConfig: {
            data: this.getCurrentPipelineStageOptions(),
            allowSearch: true,
            searchableKey: "name",
          },
        },
        {
          title: "To stage",
          fieldName: FIELD_IDENTIFIERS.NEW_STAGE,
          value: DEFAULT_STAGE,
          placeholder: "Select new stage",
          type: INPUT_TYPE.DROPDOWN,
          required: true,
          dropdownConfig: {
            data: this.getCurrentPipelineStageOptions(),
            allowSearch: true,
            searchableKey: "name",
          },
        },
      ],
    };
  }

  componentDidMount() {
    const fieldsToUpdate = Utility.makeCopyOfObject(this.state.fields);

    const stepDataFields = this.props.stepData?.configuration?.fields;

    if (Utility.isEmptyObject(stepDataFields)) return;

    let selectedPipelineStages = this.getCurrentPipelineStageOptions();

    /* Get prev selected pipeline */
    const savedPipelineData = stepDataFields.find(
      (savedField) => savedField.name === FIELD_IDENTIFIERS.PIPELINE
    );
    if (!Utility.isEmptyObject(savedPipelineData.value)) {
      this.selectedPipeline = this.pipelineData.find(
        (pipeline) => pipeline.id === savedPipelineData.value
      ) || { id: savedPipelineData.value, name: "" };
      selectedPipelineStages = this.getCurrentPipelineStageOptions();
    }

    /* Update fields with previously selected data if any */
    stepDataFields.forEach((savedFieldData) => {
      if (
        Utility.isEmptyObject(savedFieldData.name) ||
        Utility.isEmptyObject(savedFieldData.value)
      )
        return;

      let fieldDataToUpdate = fieldsToUpdate.find(
        (fieldData) => fieldData.fieldName === savedFieldData.name
      );

      if (Utility.isEmptyObject(fieldDataToUpdate)) return;

      switch (savedFieldData.name) {
        case FIELD_IDENTIFIERS.PIPELINE:
          fieldDataToUpdate.value = this.selectedPipeline;
          break;
        case FIELD_IDENTIFIERS.PREV_STAGE:
          fieldDataToUpdate.value = selectedPipelineStages.find(
            (option) => option.id === savedFieldData.value
          ) || { id: savedFieldData.value, name: "" };
          fieldDataToUpdate.dropdownConfig.data = selectedPipelineStages;
          break;
        case FIELD_IDENTIFIERS.NEW_STAGE:
          fieldDataToUpdate.value = selectedPipelineStages.find(
            (option) => option.id === savedFieldData.value
          ) || { id: savedFieldData.value, name: "" };
          fieldDataToUpdate.dropdownConfig.data = selectedPipelineStages;
          break;
        default:
      }
    });

    this.setState({ fields: fieldsToUpdate });
  }

  getCurrentPipelineStageOptions = () => {
    const stageOptions = Utility.makeCopyOfObject(
      this.selectedPipeline?.stages || []
    );
    stageOptions.unshift(DEFAULT_STAGE);

    return stageOptions;
  };

  onSave = () => {
    const fields = this.getDataToSave();
    if (!this.isValidPipeline()) {
      showAlert(
        "Pipeline Error",
        "Please select a valid pipeline."
      );
      return;
    }
    if (this.isValidateStages(fields)) {
      showAlert(
        "Same Stage Error",
        "You can't select same stage in from and to"
      );
      return;
    }
    this.props.onSave({ fields: fields });
    this.props.onClose();
  };

  getDataToSave = () => {
    const fieldsData = this.state.fields.map((field) => ({
      name: field.fieldName,
      value: field.value.id,
    }));
    return fieldsData;
  };

  isValidateStages = (fieldsData) => {
    if (fieldsData?.length > 0) {
      let prevStage;
      let newStage;
      fieldsData.forEach((d) => {
        if (d.name === FIELD_IDENTIFIERS.PREV_STAGE && d.value !== "any") {
          prevStage = d;
        } else if (
          d.name === FIELD_IDENTIFIERS.NEW_STAGE &&
          d.value !== "any"
        ) {
          newStage = d;
        } else if (d.name === FIELD_IDENTIFIERS.PIPELINE) {
        }
      });

      return prevStage && newStage && prevStage.value === newStage.value;
    }

    return false;
  };

  isValidPipeline = () => {
    const selectedPipeline =
      this.pipelineData?.find((p) => p.id === this.selectedPipeline.id) || null;
    if (!selectedPipeline) {
      return;
    }
    return true
  };

  onValueChanged = (allFields: any[], updatedField: any) => {
    let fieldsToUpdate = Utility.makeCopyOfObject(this.state.fields);

    switch (updatedField.fieldName) {
      case FIELD_IDENTIFIERS.PIPELINE:
        /* Pipeline Input */
        this.selectedPipeline = updatedField.value;
        fieldsToUpdate[0].value = updatedField.value;
        /* Stage Inputs */
        const stageOptions = this.getCurrentPipelineStageOptions();
        fieldsToUpdate[1].dropdownConfig.data = stageOptions;
        fieldsToUpdate[2].dropdownConfig.data = stageOptions;

        fieldsToUpdate[1].value = stageOptions[0];
        fieldsToUpdate[2].value = stageOptions[0];
        break;
      case FIELD_IDENTIFIERS.PREV_STAGE:
      case FIELD_IDENTIFIERS.NEW_STAGE:
        fieldsToUpdate = allFields;
        break;
      default:
    }

    this.setState({
      fields: fieldsToUpdate,
    });
  };

  renderHeader() {
    return (
      <div className="row justify-content-between pt-s pb-s bg-gray3 p-h-m">
        <DKLabel text={`Candidate Stage Changed`} className="fw-m" />
        <DKButton
          icon={DKIcons.ic_close}
          className=""
          onClick={this.props.onClose}
        />
      </div>
    );
  }
  dropdownOptionRenderer = (index, option) => {
    return <DKLabel text={option.name} className="text-align-left" />;
  };
  fieldDisplayFormatter = (selectedOption) => {
    return selectedOption.name;
  };
  renderBody() {
    return (
      <div className="column flex-1 p-v-r p-h-l">
        <DKLabel
          className="text-align-left"
          text="Select the conditions to trigger this workflow.."
        />
        <DKInputForm
          className="deal-stage-trigger-form mt-r"
          fields={this.state.fields.map((field) => ({
            ...field,
            dropdownConfig: {
              ...field.dropdownConfig,
              renderer: this.dropdownOptionRenderer,
            },
            formatter: this.fieldDisplayFormatter,
          }))}
          direction={INPUT_VIEW_DIRECTION.VERTICAL}
          onChange={this.onValueChanged}
        />
      </div>
    );
  }
  renderBottom() {
    return (
      <div className="row-reverse pb-l p-h-m">
        <DKButton
          title="Save"
          className="bg-button text-white ml-m"
          onClick={() => {
            this.onSave();
          }}
        />

        <DKButton
          title="Cancel"
          className="bg-gray1 border-m"
          onClick={this.props.onClose}
        />
      </div>
    );
  }
  render() {
    return (
      <Popup
        popupWindowStyles={{
          padding: 0,
          width: 400,
          maxWidth: "90vw",
          overflowY: "visible",
        }}
      >
        {this.renderHeader()}
        {this.renderBody()}
        {this.renderBottom()}
      </Popup>
    );
  }
}

export default StageChangeTriggerPopup;
